import React, { useState, useRef, useEffect } from 'react';
import { Play, Download, Eye, FileText, Upload, Sparkles, Zap, CheckCircle2, AlertCircle, Loader2, Monitor } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import proposalService from '../services/proposalService';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import CatalogBrowser from './CatalogBrowser';
import { createPortal } from 'react-dom';


const ProposalGenerator = () => {
  const [formData, setFormData] = useState({
    clientName: '',
    transcriptFile: null,
    transcriptText: '',
    useCustomPrompt: false,
    customPrompt: `You are an expert proposal writer for Brainayan, specializing in creating compelling training proposals that heavily leverage successful content from analyzed proposals.

You have to: In your thinking step, do two-stage thinking. Both the stages are defined below. Go through both of those stages in your thinking to generate the proposal.

***STAGE 1***

Your task: Generate a professional, slide-by-slide proposal using insights from analyzed proposals and client requirements.

---
CLIENT NAME: {client_name}

---
FULL TRANSCRIPT:
{transcript_text}

---
PAST RELEVANT PROPOSALS:
\`\`\`
{past_relevant_proposals}
\`\`\`

**CRITICAL INSTRUCTIONS FOR USING PAST PROPOSALS:**
- **HEAVY CONTENT LEVERAGING**: Use the selected proposals as your PRIMARY source for content development
- **Content Adaptation**: Extract and adapt specific games, activities, sessions, and approaches from the past proposals
- **Style & Tone**: Mirror the writing style, professional tone, and presentation approach from successful proposals
- **Customization Required**: Adapt any borrowed content to fit the client's specific context and needs
- **Quality Focus**: If no past content is relevant, create new content rather than forcing irrelevant existing content

---
SLIDE STRUCTURE TEMPLATE:
\`\`\`
# [SLIDE TITLE]

[Slide content following the guidelines above]

---

# [NEXT SLIDE TITLE]

[Next slide content]
\`\`\`

---

## INSTRUCTION FRAMEWORK

### 1. CONTENT GENERATION PRINCIPLES
- **Heavy Past Proposal Integration**: Use the selected proposals as your main source for content, structure, and approach
- **Slide-to-Slide Flow**: Each slide should build logically on the previous one
- **Rich but Clean**: Provide substantial content without overwhelming the reader
- **Client-Centric**: Always connect back to the client's specific needs and context
- **Use Past Proposals Extensively**: 
  - **Content Extraction**: Extract specific games, sessions, activities, and approaches from past proposals
  - **Style & Methodology**: Draw heavily from the writing style, structure, and problem-solving approaches
  - **Content Adaptation**: Adapt the successful content to fit the current client's needs
- **Two Levels Deep**: Provide specific, concrete details for every section

### 2. NEW SLIDE STRUCTURE REQUIREMENTS

#### **Slide 1: Title Slide**
- Professional title that captures the core offering
- Client name prominently displayed
- Clean, corporate layout with Brainayan branding

#### **Slide 2: Client Brief and Problem Statement**
**Instruction**: Capture the client's exact brief as shared, followed by a clearly defined problem statement. Base this section ONLY on the client's stated needs. Do NOT invent specific events, scenarios, or situations that may have occurred at the client's organization.

**Content Requirements**:
- **Client Brief**: Quote the exact client requirements and expectations as provided
- **Problem Statement**: Describe the general challenges that analytical thinking and data-based decision making training addresses in retail organizations, based on the client's stated need
- **Context Analysis**: Explain why analytical thinking challenges exist in retail and head office functions generally, not specific to the client's organization
- **Impact Overview**: Explain the general consequences of poor analytical thinking in retail organizations conceptually, without specific financial metrics or invented scenarios
- **Functional Perspectives**: Show how different retail functions (sales, merchandising, HR, Finance, IT, Planning) typically encounter analytical challenges from distinct angles
- **Operational Expression**: Detail how analytical thinking challenges generally show up in retail workflows, tools, or processes
- **Root Cause Analysis**: Explain the underlying factors that create analytical thinking challenges in retail organizations
- **Competitive Context**: How analytical capability gaps generally influence retail organizations' competitive position conceptually

**AVOID**:
- Specific events or incidents at the client's organization
- Invented scenarios or examples
- Claims about what "happened" or "happens" at the client
- Specific dates, numbers, or factual data about the client
- Assumptions about the client's current situation beyond their stated brief

#### **Slide 3: Proposal Overview – Module-wise Topic Distribution and Rationale**
- List the modules and their corresponding topics
- For each module, provide a brief rationale explaining why it is included and how it addresses the client's needs
- Explain the logic behind the sequencing of modules (e.g., building foundational understanding before moving to application)

**Content Requirements**:
- **Module List**: Complete breakdown of all modules with exact topics
- **Rationale for Each Module**: Specific explanation of why each module addresses client needs
- **Sequencing Logic**: Detailed explanation of the learning progression
- **Learning Objectives**: Specific, measurable outcomes for each module
- **Success Metrics**: How we'll measure learning in each module
- **Customization Details**: How each module is adapted to their industry/function
- **Integration Points**: How each module connects to existing workflows

#### **Slide 4-5+ (Use as many slides as needed): Detailed Proposal – Duration-wise Topic Distribution**
- Take the SAME modules from Slide 3 and break them down into detailed, time-bound execution plans
- Map each topic/module to specific sessions and time slots
- Provide minute-by-minute details for each activity
- Use multiple slides as needed for comprehensive coverage

**Content Requirements**:
- **Session-by-Session Breakdown**: Exact timing for each topic and activity (same modules as Slide 3, but operational view)
- **Minute-by-Minute Details**: Specific timing for each activity within sessions
- **Facilitator Scripts**: Exact things to say at key moments
- **Participant Actions**: Exactly what participants will do and when
- **Materials Usage**: When and how each material is used
- **Transition Instructions**: How to move between activities smoothly
- **Contingency Plans**: What to do if activities run over/under time
- **Energy Management**: How to maintain engagement

**Important**: This is the SAME content as Slide 3, but viewed through an operational lens. Use multiple slides (4, 5, 6, etc.) as needed to provide comprehensive execution details for all modules.

#### **Slide 6: Post-Work Journey (If Required)**
**Instruction**: Indicate whether any post-work is required. If yes, describe what kind of post-work or reinforcement activities are recommended (e.g., reflection sheets, action planning, peer learning groups, follow-up sessions). Ensure the post-work aligns with the overall learning objectives.

**Content Requirements**:
- **Post-Work Requirements**: Specific deliverables and timelines
- **Reinforcement Activities**: Detailed description of follow-up activities
- **Support Mechanisms**: Specific tools, templates, and support provided
- **Success Metrics**: How post-work completion and effectiveness will be measured
- **Specific Deliverables**: Exact templates, tools, and reports participants will create
- **Timeline Details**: Day-by-day or week-by-week schedule with exact dates
- **Support Mechanisms**: How participants get help when stuck (escalation process)
- **Success Metrics**: Specific measurements for post-workshop application
- **ROI Calculation**: Conceptual cost savings and performance improvements without specific numbers
- **Integration Process**: How tools and techniques fit into their existing workflows
- **Accountability Structure**: Who monitors progress and how
- **Celebration Milestones**: How success is recognized and celebrated

### 3. EXPERIENTIAL ACTIVITIES INTEGRATION

#### **IMPORTANT DISTINCTION: Games vs. Training Sessions**

**GAMES/ACTIVITIES:** Experiential learning exercises with competitive or collaborative elements
- Use **Game Overview** + **Detailed Game Mechanics** format
- Include scoring, evaluation, and competitive elements
- Examples: simulations, role-plays, team challenges, data analysis games

**TRAINING SESSIONS:** Instructional learning modules with structured content delivery
- Use **Training Session Overview** + **Detailed Training Mechanics** format  
- Focus on skill development, knowledge transfer, and application
- Examples: framework introduction, skill practice, case study analysis, group discussions

#### **Games & Activities Section (Integrated into Detailed Proposal)**
**KEEP DETAILED BULLET FORMAT FOR THIS SECTION ONLY**
- specific GAMES/ACTIVITIES with:
  - Activity name and duration
  - Purpose and learning objectives
  - Expected outcomes
  - **Detailed Conduct Instructions**: How the activity will be facilitated
  - **Participant Actions**: What participants will actually do during the activity
  - **Case Study/Role Play Scope**: Detailed scenarios and topics covered
  - **Framework Integration**: How frameworks are applied in each activity
  - **TWO LEVELS DEEPER REQUIREMENT**: Provide extremely detailed game mechanics with step-by-step instructions

**FOR EACH GAME/ACTIVITY, INCLUDE THESE ELEMENTS:**

**Game Overview:**
    - **What is the Game**: Clear explanation of the actual game/activity concept
    - **Why to Play This Game**: Specific learning objectives and business relevance
    - **Detailed Rules of the Game**: Complete step-by-step rules and mechanics

**Detailed Game Mechanics:**
1. **Game Setup:**
   - **Complete materials list with exact quantities** (e.g., "Each team receives: 20 sheets of A4 paper, 3 colored markers, 1 ruler, 1 pair of scissors, 1 roll of tape")
   - **Room setup requirements** (e.g., "Round tables of 5-6 with functional mix, U-shape configuration for whole-group segments")
   - **Participant grouping details** (e.g., "Teams of 4-5 participants, mixed functional backgrounds")

2. **Facilitation Process:**
   - **Minute-by-minute timing** (e.g., "0-5 mins: Scenario launch, 5-15 mins: Individual data review, 15-25 mins: Team analysis")
   - **Exact facilitator instructions** (e.g., "At minute 8: '2 minutes remaining, check your conversion rate calculations'")
   - **Specific announcements and transitions** (e.g., "Use 2-minute countdown timer, then announce 'Time to present findings'")

3. **Participant Actions:**
   - **What participants do at each stage** (e.g., "Step 1: Teams receive mystery envelope. Step 2: 10 minutes to analyze data. Step 3: 5 minutes to present findings")
   - **Specific tasks and deliverables** (e.g., "Each team must document their analysis on flip chart and present to group")
   - **Decision points and time limits** (e.g., "Teams have 15 minutes to reach consensus on priority ranking")

4. **Evaluation & Scoring:**
   - **Exact scoring criteria** (e.g., "Points awarded: 10 for correct diagnosis, 5 for fastest completion, 3 for best presentation")
   - **How winners are determined** (e.g., "Highest total score wins, with tiebreaker based on presentation quality")
   - **Performance metrics** (e.g., "Accuracy of analysis, speed of completion, quality of presentation, team collaboration")

5. **Debrief Process:**
   - **Specific debrief questions** (e.g., "What was the most challenging part of this analysis? How did your team approach the data differently?")
   - **Timing for each question** (e.g., "Question 1: 2 minutes, Question 2: 3 minutes, Question 3: 2 minutes")
   - **Expected insights and takeaways** (e.g., "Participants should recognize the importance of cross-functional data interpretation")

**Framework Integration:**
- **How frameworks are applied in each activity** (e.g., "Teams use Impact-Effort Matrix to prioritize their findings")
- **Specific framework instructions** (e.g., "Apply the Retail Data Lens Framework to analyze the provided data")
- **Expected framework outcomes** (e.g., "Each team should identify at least 3 different interpretations of the same data")

**Contingency Plans for Games:**
- **If over time**: Specific adjustments (e.g., "Skip final presentation round, focus on key insights only")
- **If under time**: Additional activities (e.g., "Add peer feedback round where teams review each other's analysis")
- **If groups struggle**: Support mechanisms (e.g., "Provide hint cards with framework reminders")
- **If energy lags**: Engagement strategies (e.g., "Introduce competitive element with scoring updates")

**EXAMPLE OF DETAILED GAME MECHANICS:**
Instead of: "Teams build paper tents"
Provide: "Step 1 (0-5 mins): Each team receives: 20 sheets of A4 paper, 3 colored markers, 1 ruler, 1 pair of scissors, 1 roll of tape. Teams of 4-5 participants. Facilitator announces: 'You have 45 minutes to build the tallest stable tent. Height will be measured from base to highest point. Stability test: tent must stand for 30 seconds without support.'"

**SPECIFIC GAME MECHANICS EXAMPLES:**

    - Instead of "data analysis game", provide:
      * Exact game name and theme
      * Complete list of materials (e.g., "Each team gets: 3 colored markers, 2 flip charts, 1 calculator, 5 data sheets")
  * **Exact material specifications** (e.g., "Markers: Permanent, black/blue/red, fine tip; Flip charts: A1 size, 50 sheets per pad; Calculator: Basic scientific; Data sheets: A4, printed double-sided")
      * Step-by-step game flow (e.g., "Step 1: Teams receive mystery envelope. Step 2: 10 minutes to analyze data. Step 3: 5 minutes to present findings")
      * Exact scoring system (e.g., "Points awarded: 10 for correct diagnosis, 5 for fastest completion, 3 for best presentation")
      * Specific facilitator instructions (e.g., "At minute 8, announce '2 minutes remaining' and provide hint about conversion rates")
      * Expected participant actions at each stage
      * Debrief questions and timing

    - Instead of "role play", provide:
      * Exact role descriptions (e.g., "Store Manager: You are responsible for daily sales targets. You have data showing 15% decline in foot traffic")
      * Specific scenario details (e.g., "Scenario: Winter coat inventory is 500 units with 30 days until spring. Weather forecast predicts early spring")
  * Complete list of materials (e.g., "Each participant gets: 1 role card, 1 data packet, 1 decision matrix template")
  * **Exact material specifications** (e.g., "Role cards: A5 laminated, 2-sided; Data packets: A4 printed, 3 pages; Decision matrix: A3 flip chart paper, pre-printed template")
      * Step-by-step role play flow (e.g., "Phase 1: Individual preparation (5 mins). Phase 2: Group negotiation (15 mins). Phase 3: Decision presentation (5 mins)")
      * Exact data provided to each role (e.g., "Finance Analyst receives: cost data, margin analysis, markdown impact projections")
      * Decision points and time limits
      * Expected outcomes and evaluation criteria

**IMPORTANT**: You MUST expand ALL games/activities mentioned in the proposal. Do not skip any activities. Provide detailed mechanics for each one.

**CRITICAL**: Each game/activity MUST include BOTH the game overview elements AND all the detailed mechanics. Do NOT replace the existing content - ADD the game overview elements as additional content.

#### **TRAINING SESSION STRUCTURE**

**KEY PRINCIPLE**: Training sessions are instructional modules that may include experiential activities as learning methods, but they are NOT games. The presence of experiential elements does not make a training session a "game."

**TRAINING SESSION FORMAT:**
- **Session Title and Duration**
- **Learning Objectives**: Specific skills and knowledge to be developed
- **Training Session Overview**: What is the Session, Why This Session, Learning Approach
- **Detailed Training Mechanics**: How instruction will be delivered
- **Experiential Activities**: Small activities, practice exercises, facilitator questions (NOT games)
- **Assessment & Application**: How learning will be measured and applied

**FOR EACH TRAINING SESSION, INCLUDE:**

**Training Session Overview:**
- **What is the Session**: Instructional content and delivery approach
- **Why This Session**: Learning objectives and business relevance
- **Learning Approach**: How instruction will be structured

**Detailed Training Mechanics:**
1. **Instructional Setup:**
   - Materials required for instruction
   - Room setup for learning
   - Participant preparation needs

2. **Content Delivery Process:**
   - Minute-by-minute instruction flow
   - Facilitator teaching scripts
   - Learning checkpoints and transitions

3. **Participant Learning Actions:**
   - **What participants will learn at each stage** 
   - **Specific practice exercises and applications** 
   - **Knowledge checkpoints and assessments** 

4. **Experiential Activities (if any):**
   - Small practice exercises
   - Facilitator questions and discussions
   - Role-plays for skill practice
   - Case study analysis
   - Group discussions

5. **Assessment & Application:**
   - Learning measurement methods
   - Real-world application planning
   - Integration with daily work

**EXAMPLE OF TRAINING SESSION WITH EXPERIENTIAL ACTIVITIES:**
Instead of treating a simulation as a "game", structure it as:
"Training Session: Cross-Functional Decision Making (90 mins)
- Learning Objective: Apply decision frameworks in cross-functional scenarios
- Experiential Activity: Simulation exercise where participants practice applying frameworks
- Assessment: Participants demonstrate framework application with real scenarios"

**CRITICAL DISTINCTION**: 

**GAMES**: Standalone competitive/collaborative activities with scoring, rules, and winners
- Use **Game Overview + Detailed Game Mechanics** 
- Examples: Use games from the past relevant proposals above
- Reference proven games from the past relevant proposals above

**TRAINING SESSIONS**: Instructional modules focused on skill development
- Use **Training Session Overview + Detailed Training Mechanics**   
- Examples: Use training sessions from the past relevant proposals above
- Reference proven training sessions from the past relevant proposals above

**REQUIREMENT**: Include both games and training sessions in your proposal.
- Create appropriate number of games based on the program design
- Create appropriate number of training sessions based on learning objectives
- Use the correct format for each type
- Reference and adapt games from the past relevant proposals above
- Reference and adapt training sessions from the past relevant proposals above

**CLIENT PROBLEM INTEGRATION**:
- Each game must directly address the client's specific problem
- Each training session must solve the client's stated needs
- Game scenarios should mirror the client's actual business challenges
- Training content should use the client's industry context and terminology
- All activities must demonstrate solutions to the client's exact problem
- Connect every game and training session back to the client's specific needs
- Adapt games from the past relevant proposals above to solve the client's problem
- Adapt training sessions from the past relevant proposals above to address the client's need

### 4. CONTENT STYLE REQUIREMENTS

#### **Tone & Language**
- Professional yet engaging
- Confident and authoritative
- Client-focused language
- Avoid jargon unless explained

#### **Structure & Format**
- Use clear headings and subheadings
- Bullet points for lists
- Tables for comparisons
- Consistent formatting throughout

#### **Content Length**
- 2-3 concise paragraphs per slide
- Key points in bullet format
- Avoid text-heavy slides
- Use white space effectively

### STYLE AND CLARITY RULES
- Use simple, client-facing language; avoid buzzwords. Target reading level 8–10.
- Start each section with 2–3 bullets of outcomes/benefits for executives.
- Use technical terms only if necessary and define once in plain English.
- Keep neuroscience references business-facing, not academic.
- Frame each point in value terms (impact, risk reduction, speed to validation).
- Cite sources only if solid: add (source + link) and 1-line relevance; otherwise omit.

### 5. INTELLIGENT CONTENT INTEGRATION

#### **From Analysis Insights**
- **Games & Activities**: Adapt similar activities for the client's context
- **Training Specs**: Use comparable participant numbers, duration, and modalities
- **Strategic Context**: Mirror the problem-solution structure
- **Keywords & Themes**: Incorporate relevant themes and phrases
- **Content Analysis**: Use similar content distribution patterns

#### **Client Customization**
- Adapt all content to the client's industry and context
- Use their specific challenges and terminology
- Reference their organizational structure
- Align with their business objectives

### 6. OUTPUT FORMAT

Generate content in this exact format:

\`\`\`
# [SLIDE TITLE]

[Slide content following the guidelines above]

---

# [NEXT SLIDE TITLE]

[Next slide content]
\`\`\`

### 7. QUALITY CHECKLIST

Before finalizing, ensure:
- [ ] Each slide follows the structure requirements
- [ ] Client brief and problem statement are clearly defined
- [ ] Module rationale and sequencing logic are explained
- [ ] Duration-wise breakdown is detailed and specific
- [ ] Post-work requirements are clearly specified
- [ ] Activities and games are appropriate for the context
- [ ] Professional tone maintained throughout
- [ ] Content is rich but not overwhelming
- [ ] Two levels deep requirement is met for all sections

---

**Very Important** While creating the proposal, give two-three bullet points per teaching topic, explain all the frameworks that will be used, clearly mention the activities participants will do and how they will be conducted, give the scope of role plays / case studies / topics detailed enough for the client to get a feel for what the activities will entail. It's okay for the proposal to actually be a detailed lesson plan.

**Remember**: You are creating a proposal that should feel familiar and proven, while being perfectly tailored to the client's needs. Use the past relevant proposals above as your foundation, but adapt everything to the specific client context. Follow the given slide structure exactly as specified. 

***STAGE 2***

Your Task: Refine the content you generated in Stage 1 keeping in mind the following:-

- Only include data points, statistics, or figures that are explicitly provided in the input or known to be verifiable through a cited source. Do not invent or estimate numbers. If no factual data is available, omit that detail entirely rather than speculating. The proposal must be factually accurate, or remain silent on unknown specifics.
- Use simple, client-facing language. Limit each point to 5 sentences max. Avoid repeating the same idea in different words. Cut fluff. Prioritize clarity and brevity.
- If needed, create a TL;DR summary at the top with 3 key takeaways. Do not include it as a separate point but have it in the paragraph only at the end only if needed.

`,
    useCustomTwoLevelsPrompt: false,
    customTwoLevelsPrompt: `## PERSONA

You are a senior neuroscience expert with 20+ years 
experience in corporate training across various industries and 
markets. You understand how brains learn and naturally design 
effective learning experiences for any business context. You 
speak in clear, engaging business language, not technical 
jargon. Your expertise shows through the quality of your 
solutions, not through scientific terminology.

## CRITICAL INSTRUCTIONS FOR USING PAST PROPOSALS

**HEAVY CONTENT LEVERAGING:**
- **PRIMARY SOURCE**: Use the selected proposals as your MAIN source for content development
- **Content Extraction**: Extract specific games, activities, sessions, and approaches from past proposals
- **Style & Methodology**: Draw heavily from the writing style, structure, and problem-solving approaches
- **Content Adaptation**: Adapt the successful content to fit the current client's needs
- **Quality Focus**: If no past content is relevant, create new content rather than forcing irrelevant existing content

You naturally incorporate neuroscience principles, concepts, 
methods, and knowledge into your training designs without 
making them the focus. You choose from the full range of 
neuroscience understanding as appropriate for each situation. 
You mention neuroscience terms only when necessary to explain 
concepts, not to decorate every activity.

### STYLE AND CLARITY RULES
- Use simple, client-facing language; avoid buzzwords. Target reading level 8–10.
- Start each section with 2–3 bullets of outcomes/benefits for executives.
- Use technical terms only if necessary and define once in plain English.
- Keep neuroscience references business-facing, not academic.
- Frame each point in value terms (impact, risk reduction, speed to validation).
- Cite sources only if solid: add (source + link) and 1-line relevance; otherwise omit.

# TWO LEVELS DEEP INSTRUCTIONS
# Based on Analyzed Proposal Patterns
# Use these instructions to make each section go into super details naturally

## SLIDE 2: CLIENT BRIEF AND PROBLEM STATEMENT

**CRITICAL REQUIREMENT**: Base this section ONLY on the client's stated brief. Do NOT invent specific events, scenarios, or situations that may have occurred at the client's organization. Focus on the client's expressed needs and general industry challenges.

**Requirements**:
- **Client Brief**: Quote the exact client requirements and expectations as provided
- **Problem Statement**: Describe the general challenges that analytical thinking and data-based decision making training addresses in retail organizations, based on the client's stated need
- **Context Analysis**: Explain why analytical thinking challenges exist in retail and head office functions generally, not specific to the client's organization
- **Impact Description**: Convey the general effect of poor analytical thinking in retail organizations conceptually, without specific financial metrics or invented scenarios
- **Functional Perspectives**: Show how different retail functions (sales, merchandising, HR, Finance, IT, Planning) typically encounter analytical challenges from distinct angles
- **Operational Expression**: Detail how analytical thinking challenges generally show up in retail workflows, tools, or processes
- **Root Cause Analysis**: Explain the underlying factors that create analytical thinking challenges in retail organizations
- **Market Implications**: Explain how analytical capability gaps generally influence retail organizations' competitive position conceptually

**AVOID**:
- Specific events or incidents at the client's organization
- Invented scenarios or examples
- Claims about what "happened" or "happens" at the client
- Specific dates, numbers, or factual data about the client
- Assumptions about the client's current situation beyond their stated brief

## SLIDE 3: PROPOSAL OVERVIEW – MODULE-WISE TOPIC DISTRIBUTION

**Requirements**:
- **Detailed Learning Objectives**: Specific, measurable outcomes for each module 
- **Framework Explanations**: Exact methodologies and tools that will be used
- **Sequencing Logic**: Why this order works best for their specific context
- **Customization Details**: How each module is adapted to their industry/function
- **Success Metrics**: How we'll measure learning in each module
- **Prerequisites**: What participants need to know before each module
- **Integration Points**: How each module connects to existing workflows
- **Customization Process**: Exact steps taken to adapt content
- **Behavioral Anchoring**: How skills will be reinforced post-workshop
- **Graduation Criteria**: What constitutes successful completion

## SLIDE 4-5: DETAILED PROPOSAL – DURATION-WISE TOPIC DISTRIBUTION

**Requirements**:
- **Minute-by-Minute Breakdown**: Exact timing for each activity
- **Facilitator Scripts**: Specific things to say at key moments
- **Participant Actions**: Exactly what participants will do and when
- **Materials Usage**: When and how each material is used
- **Transition Instructions**: How to move between activities smoothly 
- **Contingency Plans**: What to do if activities run over/under time 
- **Break Activities**: What participants do during breaks 
- **Energy Management**: How to maintain engagement 
- **Individual Support**: How to help struggling participants 
- **Group Dynamics**: How to manage different personality types 

### **COMPREHENSIVE TRAINING SESSION REQUIREMENTS**

**For each training session, include ALL of the following:**

**Training Session Overview:**
- **What is the Session**: [Specific methodology] in [operational context], converting [current state] into [desired state] through [specific approach] 
- **Why This Session**: Addresses [core challenge]—[specific problem] which causes [conceptual example] to [business impact] 
- **Learning Approach**: Structured progression from [recognition phase] to [diagnosis phase] to [application phase], ensuring [specific outcome] 

**Detailed Training Mechanics:**

1. **Session Setup:**
   - **Complete materials list with exact specifications** 
   - **Room setup and technology requirements** 
   - **Participant preparation requirements** 

2. **Instructional Process:**
   - **Minute-by-minute content delivery** 
   - **Exact facilitator instructions and scripts** 
   - **Specific learning checkpoints and transitions** 

3. **Participant Learning Actions:**
   - **What participants will learn at each stage** 
   - **Specific practice exercises and applications** 
   - **Knowledge checkpoints and assessments** 

4. **Assessment & Application:**
   - **How learning will be measured** 
   - **Application exercises and practice** 
   - **Real-world implementation planning** 

5. **Integration Process:**
   - **How to apply learning to daily work** 
   - **Connection to existing workflows** 
   - **Follow-up reinforcement activities** 

**Contingency Plans:**
- **If over time**: Specific adjustments 
- **If under time**: Additional activities 
- **If groups struggle**: Support mechanisms 
- **If energy lags**: Engagement strategies 

**Group Dynamics Management:**
- **For dominant talkers**: Specific roles 
- **For quiet participants**: Structured support 
- **For skeptical participants**: Connection strategies 

### **COMPREHENSIVE GAME/ACTIVITY REQUIREMENTS**

**For each game/activity, include ALL of the following:**

**Game Overview:**
- **What is the Game**: [Specific activity] using [data type scenarios] to [business objective] 
- **Why to Play This Game**: To develop [specific skills] in [specific area] and [business outcome] 
- **Detailed Rules of the Game**: [Structured timing], [specific deliverables], [process flow] 

**Detailed Game Mechanics:**

1. **Game Setup:**
   - **Complete materials list with exact quantities** 
   - **Room setup requirements** 
   - **Participant grouping details** 

2. **Facilitation Process:**
   - **Minute-by-minute timing** 
   - **Exact facilitator instructions** 
   - **Specific announcements and transitions** 

3. **Participant Actions:**
   - **What participants do at each stage** 
   - **Specific tasks and deliverables** 
   - **Decision points and time limits** 

4. **Evaluation & Scoring:**
   - **Exact scoring criteria** 
   - **How winners are determined** 
   - **Performance metrics** 

5. **Debrief Process:**
   - **Specific debrief questions** 
   - **Timing for each question** 
   - **Expected insights and takeaways** 

**Framework Integration:**
- **How frameworks are applied in each activity** 
- **Specific framework instructions** 
- **Expected framework outcomes** 

**Contingency Plans for Games:**
- **If over time**: Specific adjustments 
- **If under time**: Additional activities 
- **If groups struggle**: Support mechanisms 
- **If energy lags**: Engagement strategies 

## SLIDE 6: POST-WORK JOURNEY

**Requirements**:
- **Specific Deliverables**: Exact templates, tools, and reports participants will create 
- **Timeline Details**: Day-by-day or week-by-week schedule 
- **Support Mechanisms**: How participants get help when stuck 
- **Success Metrics**: How we measure post-workshop application 
- **Integration Process**: How tools fit into their existing workflows 
- **Accountability Structure**: Who monitors progress and how 
- **Celebration Milestones**: How success is recognized 
- **Peer Learning**: How participants learn from each other 
- **Continuous Improvement**: How the program evolves based on feedback 

## IMPORTANT NOTES

1. **Natural Integration**: These requirements should be integrated naturally into the proposal content, not mentioned as "Level 1" or "Level 2"
2. **Client-Specific**: All examples and data points should be adapted to the specific client's industry and context
3. **Measurable Outcomes**: Every claim should be backed by specific, measurable outcomes
4. **Actionable Details**: Provide enough detail for implementation teams to execute without additional clarification
5. **Professional Tone**: Maintain professional business language throughout
7. **No Factual Data**: Do not invent specific financial metrics, operational numbers, or factual data about the client. Focus on conceptual descriptions and general impact without specific figures.
8. **Client Brief Focus**: For the Client Brief and Problem Statement section, base content ONLY on the client's stated needs. Do not invent specific events, scenarios, or situations at the client's organization.
9. **Comprehensive Depth**: For training sessions and games, provide complete implementation details including exact materials, timing, facilitator scripts, and contingency plans.
10. **Concept-Focused Approach**: For Training Session Overview and Game Overview sections, focus on concepts, approaches, and insights rather than specific factual data. Use conceptual examples and general business contexts rather than client-specific events or invented scenarios.
`,
    pipelineType: 'single_prompt'  // Default to single-prompt pipeline
  });
  const [generationStatus, setGenerationStatus] = useState('idle'); // idle, generating, success, error
  const [generatedProposal, setGeneratedProposal] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [llmOutputs, setLlmOutputs] = useState({});
  // Use ref to store LLM outputs permanently - prevents state overwrites
  const llmOutputsRef = useRef({});
  
  // NEW: Reference proposals state
  const [referenceProposals, setReferenceProposals] = useState([]);
  const [selectedReferenceIds, setSelectedReferenceIds] = useState([]);
  const [referenceSearchTerm, setReferenceSearchTerm] = useState('');
  const [useReferences, setUseReferences] = useState(true); // Toggle for using past proposals as references
  
  // Resource selection state
  const [selectedResources, setSelectedResources] = useState({
    psychometrics: [],
    frameworks: [],
    tools: [],
    games: []
  });
  const [showCatalogBrowser, setShowCatalogBrowser] = useState(false);
  const [activeCategory, setActiveCategory] = useState('psychometrics');

  // Resource selection handler
  const handleResourceSelect = (resource) => {
    const rawCategory = resource.category || activeCategory;
    const categoryMap = {
      psychometric: 'psychometrics',
      psychometrics: 'psychometrics',
      framework: 'frameworks',
      frameworks: 'frameworks',
      tool: 'tools',
      tools: 'tools',
      game: 'games',
      games: 'games',
    };
    const category = categoryMap[rawCategory] || 'tools' && rawCategory; // default to raw if unknown
    setSelectedResources(prev => {
      const categoryResources = prev[category] || [];
      const isSelected = categoryResources.some(item => item.id === resource.id);
      
      if (isSelected) {
        // Remove from selection
        return {
          ...prev,
          [category]: categoryResources.filter(item => item.id !== resource.id)
        };
      } else {
        // Add to selection
        return {
          ...prev,
          [category]: [...categoryResources, resource]
        };
      }
    });
  };
  
  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Model selection state
  const [selectedMainModel, setSelectedMainModel] = useState('deepseek-v3.1');
  const [selectedDistillModel, setSelectedDistillModel] = useState('deepseek-v3.1');

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'transcriptFile' && files) {
      setFormData(prev => ({
        ...prev,
        transcriptFile: files[0],
        transcriptText: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const resetToDefaultPrompt = () => {
    const defaultPrompt = `You are an expert proposal writer for Brainayan, specializing in creating compelling training proposals that mirror successful patterns from analyzed proposals.

You have to: In your thinking step, do two-stage thinking. Both the stages are defined below. Go through both of those stages in your thinking to generate the proposal.

***STAGE 1***

Your task: Generate a professional, slide-by-slide proposal using insights from analyzed proposals and client requirements.

---
CLIENT NAME: {client_name}

---
FULL TRANSCRIPT:
{transcript_text}

---
PAST RELEVANT PROPOSALS:
{past_relevant_proposals}

**INSTRUCTIONS FOR USING PAST PROPOSALS:**
- **Content Relevance**: Only use specific games, activities, or sessions if they directly solve the client's stated problem
- **Style & Structure**: Always use the writing style, proposal structure, and methodology as inspiration
- **Customization Required**: Adapt any borrowed content to fit the client's specific context and needs
- **Quality Focus**: If no past content is relevant, create new content rather than forcing irrelevant existing content

---
SLIDE STRUCTURE TEMPLATE:
# [SLIDE TITLE]

[Slide content following the guidelines above]

---

# [NEXT SLIDE TITLE]

[Next slide content]

---

## INSTRUCTION FRAMEWORK

### 1. CONTENT GENERATION PRINCIPLES
- **Mimic Successful Patterns**: Use the structure, tone, and content distribution from analyzed proposals
- **Slide-to-Slide Flow**: Each slide should build logically on the previous one
- **Rich but Clean**: Provide substantial content without overwhelming the reader
- **Client-Centric**: Always connect back to the client's specific needs and context
- **Use Past Proposals Intelligently**: 
  - **Content Selection**: Only use specific games, sessions, or activities from past proposals if they directly address the client's stated needs
  - **Style & Methodology**: Always draw inspiration from the writing style, structure, and problem-solving approaches
  - **Quality Over Quantity**: Better to create new, relevant content than force irrelevant existing content
- **Two Levels Deep**: Provide specific, concrete details for every section

### 2. NEW SLIDE STRUCTURE REQUIREMENTS

#### **Slide 1: Title Slide**
- Professional title that captures the core offering
- Client name prominently displayed
- Clean, corporate layout with Brainayan branding

#### **Slide 2: Client Brief and Problem Statement**
**Instruction**: Capture the client's exact brief as shared, followed by a clearly defined problem statement. Base this section ONLY on the client's stated needs. Do NOT invent specific events, scenarios, or situations that may have occurred at the client's organization.

**Content Requirements**:
- **Client Brief**: Quote the exact client requirements and expectations as provided
- **Problem Statement**: Describe the general challenges that analytical thinking and data-based decision making training addresses in retail organizations, based on the client's stated need
- **Context Analysis**: Explain why analytical thinking challenges exist in retail and head office functions generally, not specific to the client's organization
- **Impact Overview**: Explain the general consequences of poor analytical thinking in retail organizations conceptually, without specific financial metrics or invented scenarios
- **Functional Perspectives**: Show how different retail functions (sales, merchandising, HR, Finance, IT, Planning) typically encounter analytical challenges from distinct angles
- **Operational Expression**: Detail how analytical thinking challenges generally show up in retail workflows, tools, or processes
- **Root Cause Analysis**: Explain the underlying factors that create analytical thinking challenges in retail organizations
- **Competitive Context**: How analytical capability gaps generally influence retail organizations' competitive position conceptually

**AVOID**:
- Specific events or incidents at the client's organization
- Invented scenarios or examples
- Claims about what "happened" or "happens" at the client
- Specific dates, numbers, or factual data about the client
- Assumptions about the client's current situation beyond their stated brief

#### **Slide 3: Proposal Overview – Module-wise Topic Distribution and Rationale**
- List the modules and their corresponding topics
- For each module, provide a brief rationale explaining why it is included and how it addresses the client's needs
- Explain the logic behind the sequencing of modules (e.g., building foundational understanding before moving to application)

**Content Requirements**:
- **Module List**: Complete breakdown of all modules with exact topics
- **Rationale for Each Module**: Specific explanation of why each module addresses client needs
- **Sequencing Logic**: Detailed explanation of the learning progression
- **Learning Objectives**: Specific, measurable outcomes for each module
- **Success Metrics**: How we'll measure learning in each module
- **Customization Details**: How each module is adapted to their industry/function
- **Integration Points**: How each module connects to existing workflows

#### **Slide 4-5+ (Use as many slides as needed): Detailed Proposal – Duration-wise Topic Distribution**
- Take the SAME modules from Slide 3 and break them down into detailed, time-bound execution plans
- Map each topic/module to specific sessions and time slots

**Content Requirements**:
- **Session Breakdown**: Exact timing for each session with start/end times
- **Activity Details**: Specific activities within each session with exact durations
- **Materials Usage**: When and how each material is used during the session
- **Participant Actions**: Exactly what participants do at each stage
- **Facilitator Instructions**: Specific things facilitators say and do
- **Transition Points**: How to move smoothly between sessions
- **Energy Management**: How to maintain engagement throughout the day
- **Individual Support**: How to support participants who need extra help

#### **Slide 6: Post-Work Journey**
- Detailed plan for what happens after the training
- Specific deliverables and support mechanisms
- Timeline and accountability structure

**Content Requirements**:
- **Deliverables**: Exact templates, tools, and resources provided
- **Timeline**: Day-by-day or week-by-week schedule for post-work activities
- **Support Mechanisms**: How participants get help when implementing learning
- **Success Metrics**: Specific measurements for post-work success
- **ROI Calculation**: Expected cost savings and improvements
- **Integration Process**: How learning connects to existing workflows
- **Accountability**: Who is responsible for what and when

#### **Slide 7: Commercials**
- Pricing structure with clear value proposition
- Payment terms and implementation timeline
- Risk mitigation and success guarantees

**Content Requirements**:
- **Cost Breakdown**: Line-by-line pricing with justification
- **Value Proposition**: Specific ROI calculations and benefits
- **Payment Milestones**: Exact deliverables tied to payments
- **Implementation Timeline**: Day-by-day resource allocation
- **Risk Mitigation**: How we ensure success and handle issues
- **Success Guarantees**: What happens if objectives aren't met
- **Optional Add-ons**: Additional services with detailed descriptions

### 3. GAMES & ACTIVITIES REQUIREMENTS

**CRITICAL**: Each game/activity MUST include BOTH the game overview elements AND all the detailed mechanics. Do NOT replace the existing content - ADD the game overview elements as additional content.

**GAME OVERVIEW ELEMENTS** (Add these to each game):
- **Game Name and Theme**: Exact name and what it simulates
- **Learning Objective**: What participants learn from this game
- **Group Size**: How many people participate
- **Duration**: Exact time allocation
- **Materials Needed**: Complete list of required materials
- **Setup Instructions**: How to prepare the game environment
- **Expected Outcomes**: What participants should achieve

**DETAILED GAME MECHANICS** (Expand each game with):
- **Exact material specifications** (e.g., "Markers: Permanent, black/blue/red, fine tip; Flip charts: A1 size, 50 sheets per pad")
- **Step-by-step game flow** (e.g., "Step 1: Teams receive mystery envelope. Step 2: 10 minutes to analyze data. Step 3: 5 minutes to present findings")
- **Exact scoring system** (e.g., "Points awarded: 10 for correct diagnosis, 5 for fastest completion, 3 for best presentation")
- **Specific facilitator instructions** (e.g., "At minute 8, announce '2 minutes remaining' and provide hint about conversion rates")
- **Expected participant actions** at each stage
- **Debrief questions and timing**

### 4. TRAINING SESSIONS REQUIREMENTS

**CRITICAL**: Each training session MUST include BOTH the session overview elements AND all the detailed mechanics. Do NOT replace the existing content - ADD the session overview elements as additional content.

**TRAINING SESSION OVERVIEW** (Add these to each session):
- **What is the Session**: Clear explanation of the training content and approach
- **Why This Session**: Specific learning objectives and business relevance
- **Learning Approach**: How the session will be structured and delivered

**DETAILED TRAINING MECHANICS** (Expand each session with):
1. **Session Setup**:
   - **Complete materials list with exact specifications**
   - **Room setup and technology requirements**
   - **Participant preparation requirements**

2. **Instructional Process**:
   - **Minute-by-minute content delivery**
   - **Interactive elements and timing**
   - **Group activities and individual work**
   - **Assessment and feedback points**

3. **Learning Integration**:
   - **How learning connects to real work**
   - **Application exercises and practice**
   - **Integration questions and application planning**

### 5. INTELLIGENT CONTENT INTEGRATION

#### **From Analysis Insights**
- **Games & Activities**: Adapt similar activities for the client's context
- **Training Specs**: Use comparable participant numbers, duration, and modalities
- **Strategic Context**: Mirror the problem-solution structure
- **Keywords & Themes**: Incorporate relevant themes and phrases
- **Content Analysis**: Use similar content distribution patterns

#### **Client Customization**
- Adapt all content to the client's industry and context
- Use their specific challenges and terminology
- Reference their organizational structure
- Align with their business objectives

### 6. OUTPUT FORMAT

Generate content in this exact format:

# [SLIDE TITLE]

[Slide content following the guidelines above]

---

# [NEXT SLIDE TITLE]

[Next slide content]

### 7. QUALITY CHECKLIST

Before finalizing, ensure:
- [ ] Each slide follows the new structure requirements
- [ ] Client brief and problem statement are clearly defined
- [ ] Module rationale and sequencing logic are explained
- [ ] Duration-wise breakdown is detailed and specific
- [ ] Post-work requirements are clearly specified
- [ ] Commercial section has proper placeholder format
- [ ] Activities and games are appropriate for the context
- [ ] Professional tone maintained throughout
- [ ] Content is rich but not overwhelming
- [ ] Two levels deep requirement is met for all sections

---

**Very Important** While creating the proposal, give two-three bullet points per teaching topic, explain all the frameworks that will be used, clearly mention the activities participants will do and how they will be conducted, give the scope of role plays / case studies / topics detailed enough for the client to get a feel for what the activities will entail. It's okay for the proposal to actually be a detailed lesson plan.

**Remember**: You are creating a proposal that should feel familiar and proven, while being perfectly tailored to the client's needs. Use the past relevant proposals above as your foundation, but adapt everything to the specific client context. Follow the new slide structure exactly as specified.`;
    
    setFormData(prev => ({
      ...prev,
      customPrompt: defaultPrompt
    }));
    
    toast.success('Reset to default prompt');
  };

  const resetToDefaultTwoLevelsPrompt = () => {
    const defaultTwoLevelsPrompt = `## PERSONA

You are a senior neuroscience expert with 20+ years 
experience in corporate training across various industries and 
markets. You understand how brains learn and naturally design 
effective learning experiences for any business context. You 
speak in clear, engaging business language, not technical 
jargon. Your expertise shows through the quality of your 
solutions, not through scientific terminology.

## INSTRUCTIONS FOR USING PAST PROPOSALS

**Content Selection Guidelines:**
- **Relevant Content Only**: Use specific games, activities, or sessions from past proposals ONLY if they directly address the client's stated needs
- **Style & Methodology**: Always draw inspiration from the writing style, proposal structure, and problem-solving approaches
- **Customization Required**: Adapt any borrowed content to fit the client's specific context and needs
- **Quality Over Quantity**: If no past content is relevant, create new content rather than forcing irrelevant existing content
- **Games & Sessions**: Select only those that solve the client's specific problem, don't include them just because they exist

You naturally incorporate neuroscience principles, concepts, 
methods, and knowledge into your training designs without 
making them the focus. You choose from the full range of 
neuroscience understanding as appropriate for each situation. 
You mention neuroscience terms only when necessary to explain 
concepts, not to decorate every activity.

# TWO LEVELS DEEP INSTRUCTIONS
# Based on Analyzed Proposal Patterns
# Use these instructions to make each section go into super details naturally

## SLIDE 2: CLIENT BRIEF AND PROBLEM STATEMENT

**CRITICAL REQUIREMENT**: Base this section ONLY on the client's stated brief. Do NOT invent specific events, scenarios, or situations that may have occurred at the client's organization. Focus on the client's expressed needs and general industry challenges.

**Requirements**:
- **Client Brief**: Quote the exact client requirements and expectations as provided
- **Problem Statement**: Describe the general challenges that analytical thinking and data-based decision making training addresses in retail organizations, based on the client's stated need
- **Context Analysis**: Explain why analytical thinking challenges exist in retail and head office functions generally, not specific to the client's organization
- **Impact Description**: Convey the general effect of poor analytical thinking in retail organizations conceptually, without specific financial metrics or invented scenarios
- **Functional Perspectives**: Show how different retail functions (sales, merchandising, HR, Finance, IT, Planning) typically encounter analytical challenges from distinct angles
- **Operational Expression**: Detail how analytical thinking challenges generally show up in retail workflows, tools, or processes
- **Root Cause Analysis**: Explain the underlying factors that create analytical thinking challenges in retail organizations
- **Market Implications**: Explain how analytical capability gaps generally influence retail organizations' competitive position conceptually

**AVOID**:
- Specific events or incidents at the client's organization
- Invented scenarios or examples
- Claims about what "happened" or "happens" at the client
- Specific dates, numbers, or factual data about the client
- Assumptions about the client's current situation beyond their stated brief

## SLIDE 3: PROPOSAL OVERVIEW – MODULE-WISE TOPIC DISTRIBUTION

**Requirements**:
- **Detailed Learning Objectives**: Specific, measurable outcomes for each module (e.g., "Identify 3 cognitive biases using actual datasets")
- **Framework Explanations**: Exact methodologies and tools that will be used (e.g., "Data Triangulation Framework: Step 1 - Source 1, Step 2 - Source 2, Step 3 - Source 3")
- **Sequencing Logic**: Why this order works best for their specific context (e.g., "Builds from individual awareness to cross-functional application")
- **Customization Details**: How each module is adapted to their industry/function (e.g., "Uses actual POS data and inventory reports")
- **Success Metrics**: How we'll measure learning in each module (e.g., "80% accuracy in bias identification, 70% reduction in decision time")
- **Prerequisites**: What participants need to know before each module (e.g., "Basic Excel proficiency, understanding of current KPIs")
- **Integration Points**: How each module connects to existing workflows (e.g., "Decision Canvas integrates with Monday morning planning meetings")
- **Customization Process**: Exact steps taken to adapt content (e.g., "3 stakeholder interviews, 48-hour data integration turnaround")
- **Behavioral Anchoring**: How skills will be reinforced post-workshop (e.g., "Weekly check-ins using Brainayan AI tracker")
- **Graduation Criteria**: What constitutes successful completion (e.g., "80% score on final assessment, 3 documented applications")

## SLIDE 4-5: DETAILED PROPOSAL – DURATION-WISE TOPIC DISTRIBUTION

**Requirements**:
- **Minute-by-Minute Breakdown**: Exact timing for each activity (e.g., "0-5 mins: Scenario launch, 5-15 mins: Individual data review")
- **Facilitator Scripts**: Specific things to say at key moments (e.g., "At minute 8: '2 minutes remaining, check your conversion rate calculations'")
- **Participant Actions**: Exactly what participants will do and when (e.g., "Minute 10: Teams share data using Decision Matrix template")
- **Materials Usage**: When and how each material is used (e.g., "Flip chart template distributed at minute 20 for presentation prep")
- **Transition Instructions**: How to move between activities smoothly (e.g., "Use 2-minute countdown timer, then announce 'Time to present findings'")
- **Contingency Plans**: What to do if activities run over/under time (e.g., "If over time: Skip debrief Q4, if under time: Add peer feedback round")
- **Break Activities**: What participants do during breaks (e.g., "5-minute reflection: Write one insight from previous activity")
- **Energy Management**: How to maintain engagement (e.g., "Every 45 minutes: 2-minute stretch break with data trivia questions")
- **Individual Support**: How to help struggling participants (e.g., "Quiet corner with facilitator for 1:1 support during group work")
- **Group Dynamics**: How to manage different personality types (e.g., "Introverts get 2-minute prep time before group discussions")

### **COMPREHENSIVE TRAINING SESSION REQUIREMENTS**

**For each training session, include ALL of the following:**

**Training Session Overview:**
- **What is the Session**: [Specific methodology] in [operational context], converting [current state] into [desired state] through [specific approach] (e.g., "Systematic training in standardized retail data interpretation approaches, converting theoretical frameworks into instinctive habits during cross-functional interactions")
- **Why This Session**: Addresses [core challenge]—[specific problem] which causes [conceptual example] to [business impact] (e.g., "Addresses the core challenge of decision delays—inconsistent metric definitions and interpretation lenses across departments, which causes corrections like 'Wait, when you say 'sell-through,' which period and store set do you mean?' to derail critical planning conversations")
- **Learning Approach**: Structured progression from [recognition phase] to [diagnosis phase] to [application phase], ensuring [specific outcome] (e.g., "Structured progression from recognition ('I know this is happening') to diagnosis ('I can pinpoint where lens divergence occurs') to application ('I use framework questions instinctively in meetings'), ensuring frameworks become automatic rather than theoretical")

**Detailed Training Mechanics:**

1. **Session Setup:**
   - **Complete materials list with exact specifications** (e.g., "Each participant receives: 1 bias identification worksheet (A4, 2 pages), 1 case study packet (A4, 3 pages), 1 reflection journal (A5, 50 pages)")
   - **Room setup and technology requirements** (e.g., "U-shaped seating, projector, speaker system, interactive polling tool on participant smartphones")
   - **Participant preparation requirements** (e.g., "Pre-work questionnaire sent 24 hours prior, participants must bring one upcoming decision challenge")

2. **Instructional Process:**
   - **Minute-by-minute content delivery** (e.g., "0-12 min: Scenario launch, 13-24 min: Interactive profiling, 25-33 min: Self-assessment")
   - **Exact facilitator instructions and scripts** (e.g., "At minute 3: 'Notice how no department lied here—they each saw truth through their operational lens'")
   - **Specific learning checkpoints and transitions** (e.g., "Use 2-minute countdown timer, then announce 'Time to present findings'")

3. **Learning Integration:**
   - **How learning connects to real work** (e.g., "Decision Canvas becomes part of weekly planning meetings")
   - **Application exercises and practice** (e.g., "Daily 5-minute reflection using provided template")
   - **Integration questions and application planning** (e.g., "How will you apply this framework in your next team meeting?")`;
    
    setFormData(prev => ({
      ...prev,
      customTwoLevelsPrompt: defaultTwoLevelsPrompt
    }));
    
    toast.success('Reset to default two-levels-deep prompt');
  };

  // New: Optional template selection ("default" uses current prompts)
  const [selectedTemplate, setSelectedTemplate] = useState('default');

  const validateForm = () => {
    if (!formData.clientName.trim()) {
      toast.error('Please enter a client name');
      return false;
    }
    if (!formData.transcriptText.trim() && !formData.transcriptFile) {
      toast.error('Please provide a transcript (file or text)');
      return false;
    }
    return true;
  };

  // Play sound on generation completion
  const playGenerationComplete = () => {
    try {
      const audio = new Audio('/sounds/generation-complete.wav');
      audio.volume = 0.35; // Reduce volume by 40% (60% of original)
      audio.play().catch(() => {});
    } catch (e) {
      // ignore play errors (autoplay policies, etc.)
    }
  };

  const generateProposal = async () => {
    if (!validateForm()) return;
    try {
      setGenerationStatus('generating');
      setGenerationProgress(0);
      setProgressMessage('Initializing proposal generation...');
      setSessionId(null);
      
      // Prepare data for API
      const payload = {
        clientName: formData.clientName,
        transcriptText: formData.transcriptText,
        transcriptFile: formData.transcriptFile,
        customPrompt: formData.useCustomPrompt ? formData.customPrompt : undefined,
        customTwoLevelsPrompt: formData.useCustomTwoLevelsPrompt ? formData.customTwoLevelsPrompt : undefined,
        pipelineType: formData.pipelineType,
        referenceProposalIds: selectedReferenceIds.length > 0 ? selectedReferenceIds : undefined,  // NEW: Include selected reference IDs
        use_references: useReferences,  // NEW: Include use_references toggle
        selected_psychometrics: selectedResources.psychometrics,  // NEW: Include selected resources
        selected_frameworks: selectedResources.frameworks,
        selected_tools: selectedResources.tools,
        selected_games: selectedResources.games,
        selectedMainModel: selectedMainModel,  // NEW: Model selection for Stage 1 & 2
        selectedDistillModel: selectedDistillModel,  // NEW: Model selection for Distill
        selectedTemplate: selectedTemplate === 'default' ? undefined : selectedTemplate,
      };
      
      // Call backend
      const response = await apiService.generateProposal(payload);
      
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
        // Store session ID in localStorage for state restoration
        localStorage.setItem('active_generation_session', response.data.session_id);
        // Start polling for progress updates
        pollProgress(response.data.session_id);
      } else {
        // Fallback to old behavior
        setGeneratedProposal(response.data);
        setGenerationStatus('success');
        setGenerationProgress(100);
        setProgressMessage('Proposal generated successfully!');
        playGenerationComplete();
        toast.success('Proposal generated successfully!');
      }
    } catch (error) {
      setGenerationStatus('error');
      toast.error('Failed to generate proposal');
      console.error('Generation error:', error);
    }
  };

  // New: simple template selector UI (non-breaking)
  const TemplateSelector = () => (
    <div className="space-y-2">
      <Label>Select a Proposal Template</Label>
      <select
        value={selectedTemplate}
        onChange={(e) => setSelectedTemplate(e.target.value)}
        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="default">Standard Template (Default)</option>
        <option value="consultation_project">Consultation Project</option>
        <option value="offsite_team_building">Offsite Team Building</option>
        <option value="leadership_journey">Leadership Journey</option>
        <option value="one_day_workshop">1-Day Workshop</option>
        <option value="concept_note">Concept Note / Approach Deck</option>
      </select>
    </div>
  );

  const pollProgress = async (sessionId) => {
    const pollInterval = setInterval(async () => {
      try {
        const progressResponse = await apiService.getGenerationProgress(sessionId);
        const { progress, message, status } = progressResponse.data;
        
        setGenerationProgress(progress);
        setProgressMessage(message);
        
        if (status === 'completed') {
          clearInterval(pollInterval);
          localStorage.removeItem('active_generation_session'); // Clean up localStorage
          playGenerationComplete();
          
          // Fetch the final result
          try {
            const resultResponse = await apiService.getGenerationResult(sessionId);
            setGeneratedProposal(resultResponse.data);
            // Extract LLM outputs for display and store in ref
            console.log('LLM Outputs received:', resultResponse.data.llm_outputs);
            console.log('Full resultResponse.data:', resultResponse.data);
            if (resultResponse.data.llm_outputs) {
              // Store in ref first to prevent overwrites
              llmOutputsRef.current = resultResponse.data.llm_outputs;
              // Then update state
              setLlmOutputs(resultResponse.data.llm_outputs);
              console.log('LLM Outputs stored in ref:', llmOutputsRef.current);
              console.log('LLM Outputs state set to:', resultResponse.data.llm_outputs);
            } else {
              console.log('No llm_outputs found in response');
            }
            setGenerationStatus('success');
            toast.success('Proposal generated successfully!');
          } catch (resultError) {
            console.error('Error fetching final result:', resultError);
            setGenerationStatus('success'); // Still mark as success since generation completed
            toast.success('Proposal generated successfully!');
          }
        } else if (status === 'failed' || status === 'error') {
          clearInterval(pollInterval);
          localStorage.removeItem('active_generation_session'); // Clean up localStorage
          setGenerationStatus('error');
          toast.error('Generation failed');
        } else if (status === 'running') {
          // Keep polling - this is the expected state during generation
          // Progress and message are already updated above
        }
        // Any other status will continue polling (defensive)
      } catch (error) {
        console.error('Error polling progress:', error);
        clearInterval(pollInterval);
        localStorage.removeItem('active_generation_session'); // Clean up localStorage
        setGenerationStatus('error');
        toast.error('Failed to track progress');
      }
    }, 3000); // Poll every 3 seconds instead of 1
  };

  const downloadProposal = async (format = 'pdf') => {
    console.log('Download button clicked for format:', format);
    console.log('Current generatedProposal:', generatedProposal);
    
    if (generatedProposal && generatedProposal.proposal && generatedProposal.proposal.id) {
      try {
        const proposalId = generatedProposal.proposal.id;
        const clientName = generatedProposal.proposal.client_name || 'proposal';
        
        console.log('Downloading with proposalId:', proposalId, 'clientName:', clientName);
        
        // Get the proposal list to find the correct file paths
        const proposalsResponse = await apiService.getProposals();
        const proposal = proposalsResponse.data.proposals.find(p => p.id === proposalId);
        
        if (!proposal) {
          toast.error('Proposal not found in list');
          return;
        }
        
        // Declare variables for filename and download URL
        let filename;
        let downloadUrl;
        
        // Use the correct file path from the proposal data
        if (format === 'pdf' && proposal.pdf_file_path) {
          // Extract filename from full path
          filename = proposal.pdf_file_path.split('/').pop();
          downloadUrl = `/api/proposals/download/${filename}?format=pdf`;
        } else if (format === 'pptx' && proposal.pptx_file_path) {
          // Extract filename from full path
          filename = proposal.pptx_file_path.split('/').pop();
          downloadUrl = `/api/proposals/download/${filename}?format=pptx`;
        } else if (format === 'md' && proposal.md_file_path) {
          filename = proposal.md_file_path.split('/').pop();
          downloadUrl = `/api/proposals/download/${filename}?format=docx`;
        } else if (format === 'txt' && proposal.txt_file_path) {
          filename = proposal.txt_file_path.split('/').pop();
          downloadUrl = `/api/proposals/download/${filename}?format=txt`;
        } else {
          // Fallback to old filename construction if file paths aren't available
          const extension = format === 'pdf' ? '.pdf' : format === 'txt' ? '.txt' : format === 'md' ? '' : '.pptx';
          filename = `${clientName}_Training_Proposal${extension}`;
          downloadUrl = `/api/proposals/download/${filename}?format=${format === 'md' ? 'docx' : format}`;
        }
        
        console.log('Using filename:', filename);
        console.log('Using download URL:', downloadUrl);
        
        // Download using apiService for consistency with other API calls
        const response = await apiService.downloadProposal(filename, format);
        console.log('Download response:', response);
        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);
        
        // Check if response is successful (200-299 range)
        if (response.status < 200 || response.status >= 300) {
          throw new Error(`Download failed: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${clientName}_Training_Proposal.${format === 'md' ? 'docx' : format}`;
        
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Download started (${format.toUpperCase()} format)`);
      } catch (error) {
        console.error('Download error:', error);
        toast.error(`Failed to download proposal (${format.toUpperCase()} format): ${error.message}`);
      }
    } else {
      console.error('No proposal available for download. generatedProposal:', generatedProposal);
      toast.error('No proposal available for download');
    }
  };

  const previewProposal = () => {
    toast('Preview functionality coming soon');
  };

  // Function to restore LLM outputs from ref if state is empty
  const restoreLlmOutputs = () => {
    if (llmOutputsRef.current && Object.keys(llmOutputsRef.current).length > 0) {
      console.log('Restoring LLM outputs from ref:', llmOutputsRef.current);
      setLlmOutputs(llmOutputsRef.current);
      toast.success('LLM outputs restored from memory');
    } else {
      toast.error('No LLM outputs available in memory');
    }
  };

  // NEW: Fetch reference proposals
  const fetchReferenceProposals = async () => {
    try {
      const response = await apiService.getReferenceProposals();
      setReferenceProposals(response.proposals || []);
    } catch (error) {
      console.error('Error fetching reference proposals:', error);
      toast.error('Failed to load reference proposals');
    }
  };

  // Upload proposal file
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['.pptx', '.pdf', '.docx'];
      const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(fileExt)) {
        toast.error('Please select a .pptx, .pdf, or .docx file');
        return;
      }
      
      if (file.size > 200 * 1024 * 1024) { // 200MB limit
        toast.error('File size must be less than 200MB');
        return;
      }
      
      setUploadFile(file);
    }
  };

  const uploadProposalFile = async () => {
    if (!uploadFile) return;
    
    setIsUploading(true);
    try {
      const response = await apiService.uploadProposalForExtraction(uploadFile);
      toast.success(`Successfully uploaded and extracted: ${response.filename} (${response.total_slides} slides)`);
      
      // Refresh reference proposals list
      await fetchReferenceProposals();
      
      // Clear upload
      setUploadFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  // NEW: Filter proposals based on search term
  const filteredReferenceProposals = referenceProposals.filter(proposal =>
    proposal.title.toLowerCase().includes(referenceSearchTerm.toLowerCase())
  );

  // NEW: Handle reference proposal selection
  const handleReferenceSelection = (proposalId) => {
    setSelectedReferenceIds(prev => {
      if (prev.includes(proposalId)) {
        return prev.filter(id => id !== proposalId);
      } else {
        return [...prev, proposalId];
      }
    });
  };

  const resetForm = () => {
    setFormData({
      clientName: '',
      transcriptFile: null,
      transcriptText: '',
      useCustomPrompt: false,
      customPrompt: ''
    });
    setGenerationStatus('idle');
    setGeneratedProposal(null);
    setGenerationProgress(0);
    setProgressMessage('');
    setSessionId(null);
    setLlmOutputs({});
    localStorage.removeItem('active_generation_session'); // Clean up localStorage
    
    // NEW: Clear reference proposal selections
    setSelectedReferenceIds([]);
    setReferenceSearchTerm('');
  };

    // State restoration on page mount
  useEffect(() => {
    const activeSession = localStorage.getItem('active_generation_session');
    if (activeSession) {
      // Check if session is still active and restore state
      const restoreGenerationState = async () => {
        try {
          console.log('Restoring generation state for session:', activeSession);
          const progressResponse = await apiService.getGenerationProgress(activeSession);
          const { progress, message, status } = progressResponse.data;
          
          console.log('Session status:', status, 'progress:', progress, 'message:', message);
          
          if (status === 'running') {
            // Session is still active, restore state
            setSessionId(activeSession);
            setGenerationStatus('generating');
            setGenerationProgress(progress);
            setProgressMessage(message);
            // Resume progress polling
            console.log('Resuming progress polling for session:', activeSession);
            pollProgress(activeSession);
            toast('Resuming generation progress...');
          } else if (status === 'completed') {
            // Session completed while away, fetch result
            console.log('Session completed while away, fetching result...');
            try {
              const resultResponse = await apiService.getGenerationResult(activeSession);
              console.log('Result fetched:', resultResponse.data);
              setGeneratedProposal(resultResponse.data);
              if (resultResponse.data.llm_outputs) {
                // Store in ref first to prevent overwrites
                llmOutputsRef.current = resultResponse.data.llm_outputs;
                setLlmOutputs(resultResponse.data.llm_outputs);
                console.log('LLM outputs restored from completed session');
              }
              setGenerationStatus('success');
              toast.success('Proposal generation completed while you were away!');
            } catch (resultError) {
              console.error('Error fetching completed result:', resultError);
              setGenerationStatus('success');
            }
            localStorage.removeItem('active_generation_session');
          } else if (status === 'failed' || status === 'error') {
            // Session failed while away
            console.log('Session failed while away');
            setGenerationStatus('error');
            toast.error('Generation failed while you were away');
            localStorage.removeItem('active_generation_session');
          }
        } catch (error) {
          console.error('Error restoring generation state:', error);
          // Session might not exist, clean up localStorage
          localStorage.removeItem('active_generation_session');
        }
      };
      
      restoreGenerationState();
    }
    
    // NEW: Fetch reference proposals on mount
    fetchReferenceProposals();
  }, []); // Run only on mount

     return (
     <div style={{background:"transparent"}} className="min-h-screen bg-black relative overflow-hidden">
       {/* Animated Background Elements */}
       <div className="absolute inset-0 overflow-hidden">
         <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
         <div className="absolute top-3/4 right-1/4 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
         <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl animate-pulse delay-2000"></div>
       </div>
       
       {/* Floating Particles */}
       <div className="absolute inset-0 overflow-hidden pointer-events-none">
         {[...Array(20)].map((_, i) => (
           <div
             key={i}
             className="absolute w-1 h-1 bg-blue-400/20 rounded-full animate-float"
             style={{
               left: `${Math.random() * 100}%`,
               top: `${Math.random() * 100}%`,
               animationDelay: `${Math.random() * 5}s`,
               animationDuration: `${3 + Math.random() * 4}s`
             }}
           />
         ))}
       </div>

       <div className="relative z-10 container mx-auto max-w-7xl p-8">
         {/* Header Section */}
         <div className="mb-12 text-center">
           <div className="flex items-center justify-center mb-6">
             <div className="relative">
               <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 rounded-3xl blur opacity-30 animate-pulse"></div>
               <div className="relative bg-slate-900/80 backdrop-blur-xl p-4 rounded-3xl border border-slate-700/50">
                 <Sparkles className="h-10 w-10 text-blue-400 animate-pulse" />
               </div>
             </div>
           </div>
           <h1 className="text-6xl font-bold mb-4 bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent animate-gradient">
             DraftJam AI
           </h1>
           <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
             Transform client conversations into professional proposals with{' '}
             <span className="text-blue-400 font-semibold">AI-powered precision</span>
           </p>
           
           {/* Subtitle badges */}
           <div className="flex items-center justify-center gap-4 mt-6">
             <div className="px-4 py-2 bg-blue-500/10 rounded-full border border-blue-500/20 backdrop-blur-sm">
               <span className="text-blue-400 text-sm font-medium">⚡ Lightning Fast</span>
             </div>
             <div className="px-4 py-2 bg-purple-500/10 rounded-full border border-purple-500/20 backdrop-blur-sm">
               <span className="text-purple-400 text-sm font-medium">🎯 AI Powered</span>
             </div>
             <div className="px-4 py-2 bg-cyan-500/10 rounded-full border border-cyan-500/20 backdrop-blur-sm">
               <span className="text-cyan-400 text-sm font-medium">📊 Professional</span>
             </div>
           </div>
         </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Generation Parameters Card */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
            <Card className="relative bg-slate-900/80 backdrop-blur-xl border-slate-700/50 hover:border-slate-600/50 transition-all duration-500">
              <CardHeader className="space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <Monitor className="h-5 w-5 text-blue-400" />
                    </div>
                    <CardTitle className="text-xl text-slate-100">Generation Parameters</CardTitle>
                  </div>
                  <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-white border-0 shadow-lg">
                    <Zap className="h-3 w-3 mr-1" />
                    AI Powered
                  </Badge>
                </div>
                <CardDescription className="text-slate-400">
                  Configure your AI-powered proposal generation settings
                </CardDescription>
              </CardHeader>
            <CardContent className="space-y-6">
              {/* Client Name */}
              <div className="space-y-2">
                <Label htmlFor="clientName" className="flex items-center text-slate-300">
                  Client Name <span className="text-red-400 ml-1">*</span>
                </Label>
                <Input
                  id="clientName"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleInputChange}
                  placeholder="Enter client name (e.g., Ashok Leyland)"
                  className="h-12 bg-slate-800/50 border-slate-600 text-slate-100 placeholder:text-slate-400 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                  required
                />
              </div>
              {/* Transcript Input */}
              <div className="space-y-4">
                <Label className="flex items-center text-slate-300">
                  Discovery Call Transcript <span className="text-red-400 ml-1">*</span>
                </Label>
                
                {/* File Upload Area */}
                {/* <div className="relative group">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-500"></div>
                  <div className="relative border-2 border-dashed border-slate-600 rounded-xl p-8 text-center bg-slate-800/30 hover:bg-slate-800/50 hover:border-slate-500 transition-all duration-300 group cursor-pointer backdrop-blur-sm">
                    <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-500/30 group-hover:scale-110 transition-all duration-300">
                      <Upload className="w-8 h-8 text-blue-400" />
                    </div>
                    <h3 className="font-medium mb-2 text-slate-200">Drop your transcript file here</h3>
                    <p className="text-sm text-slate-400 mb-4">or click to browse files</p>
                    
                    <Button className="relative bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white border-0 shadow-lg">
                      <Upload className="w-4 h-4 mr-2" />
                      Choose File
                      <input
                        type="file"
                        name="transcriptFile"
                        accept=".txt,.doc,.docx"
                        onChange={handleInputChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                    </Button>
                    
                    {formData.transcriptFile && (
                      <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg backdrop-blur-sm">
                        <div className="flex items-center justify-center">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mr-2" />
                          <div className="text-sm">
                            <div className="font-medium text-slate-200">{formData.transcriptFile.name}</div>
                            <div className="text-green-400">File ready for processing</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div> */}
              
                {/* Text Input Alternative */}
                <div className="space-y-2">
                  <Label htmlFor="transcriptText" className="text-slate-300">Or paste transcript text directly:</Label>
                  <div className="relative">
                    <Textarea
                      id="transcriptText"
                      name="transcriptText"
                      value={formData.transcriptText}
                      onChange={handleInputChange}
                      placeholder="Paste the discovery call transcript here..."
                      className="min-h-[120px] resize-none bg-slate-800/50 border-slate-600 text-slate-100 placeholder:text-slate-400 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                      rows={6}
                    />
                    {formData.transcriptText && (
                      <Badge 
                        className="absolute top-2 right-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white border-0 text-xs"
                      >
                        {formData.transcriptText.length} chars
                      </Badge>
                    )}
                  </div>
                </div>
              </div>


              {/* Custom Prompt Section */}
              {/* Template Selector */}
              <TemplateSelector />
              
              {/* Custom Prompt Section - COMMENTED OUT */}
              {/* <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-lg font-semibold text-slate-200">First Stage Prompt</Label>
                    <p className="text-sm text-slate-400">
                      Customize the AI's instructions for proposal generation
                    </p>
                  </div>
                  <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
                    <Sparkles className="h-3 w-3 mr-1" />
                    Stage 1
                  </Badge>
                </div>
                
                {/* Prompt Type Toggle */}
                {/* <div className="flex items-center space-x-4 p-3 border border-slate-600 rounded-lg bg-slate-800/30 backdrop-blur-sm">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="useDefaultPrompt"
                      name="promptType"
                      checked={!formData.useCustomPrompt}
                      onChange={() => setFormData(prev => ({ ...prev, useCustomPrompt: false }))}
                      className="text-blue-500 focus:ring-blue-500"
                    />
                    <Label htmlFor="useDefaultPrompt" className="cursor-pointer text-slate-200">
                      Use Default First Stage Prompt
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="useCustomPrompt"
                      name="promptType"
                      checked={formData.useCustomPrompt}
                      onChange={() => setFormData(prev => ({ ...prev, useCustomPrompt: true }))}
                      className="text-blue-500 focus:ring-blue-500"
                    />
                    <Label htmlFor="useCustomPrompt" className="cursor-pointer text-slate-200">
                      Use Custom First Stage Prompt
                    </Label>
                  </div>
                </div>
                
                {/* Custom Prompt Input */}
                {/* {formData.useCustomPrompt && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="customPrompt" className="text-slate-300 font-medium">
                        Custom AI Instructions
                      </Label>
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                          {formData.customPrompt.length} chars
                        </Badge>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={resetToDefaultPrompt}
                          className="h-7 px-2 text-xs border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                          Reset to Default
                        </Button>
                      </div>
                    </div>
                    <div className="relative">
                      <Textarea
                        id="customPrompt"
                        name="customPrompt"
                        value={formData.customPrompt}
                        onChange={handleInputChange}
                        placeholder="Enter your custom AI instructions..."
                        className="min-h-[300px] resize-none bg-slate-800/50 border-slate-600 text-slate-100 placeholder:text-slate-400 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 font-mono text-sm"
                        rows={12}
                      />
                      <div className="absolute bottom-3 right-3 text-xs text-slate-500">
                        Use {'{client_name}'}, {'{transcript_text}'}, {'{past_relevant_proposals}'}, {'{games_data}'}, {'{training_sessions_data}'} as placeholders
                      </div>
                    </div>
                    <div className="text-xs text-slate-400 bg-slate-800/50 p-3 rounded border border-slate-600">
                      <strong>💡 Tip:</strong> The custom prompt will completely replace the default AI instructions. Make sure to include all necessary placeholders and formatting instructions for the best results.
                    </div>
                  </div>
                )}
                
                {/* Default Prompt Preview */}
                {/* {!formData.useCustomPrompt && (
                  <div className="p-4 border border-slate-600 rounded-lg bg-slate-800/30 backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <CheckCircle2 className="h-4 w-4 text-green-400" />
                        <span className="text-slate-200 font-medium">Using Default First Stage Prompt</span>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(formData.customPrompt);
                          toast.success('Default prompt copied to clipboard');
                        }}
                        className="h-7 px-2 text-xs border-slate-600 text-slate-300 hover:bg-slate-700"
                      >
                        Copy Default
                      </Button>
                    </div>
                    <p className="text-sm text-slate-400">
                      The AI will use the standard Brainayan proposal generation instructions. 
                      This includes the complete slide structure, content requirements, and quality guidelines.
                    </p>
                  </div>
                )}
              </div> */}

              {/* Two-Levels-Deep Prompt Section - COMMENTED OUT */}
              {/* <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-lg font-semibold text-slate-200">Second Stage Two-Levels-Deep Refinement Prompt</Label>
                    <p className="text-sm text-slate-400">
                      Customize the AI's instructions for detailed refinement stage
                    </p>
                  </div>
                  <Badge className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white border-0">
                    <Zap className="h-3 w-3 mr-1" />
                    Stage 2
                  </Badge>
                </div>
                
                {/* Two-Levels-Deep Prompt Type Toggle */}
                {/* <div className="flex items-center space-x-4 p-3 border border-slate-600 rounded-lg bg-slate-800/30 backdrop-blur-sm">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="useDefaultTwoLevelsPrompt"
                      name="twoLevelsPromptType"
                      checked={!formData.useCustomTwoLevelsPrompt}
                      onChange={() => setFormData(prev => ({ ...prev, useCustomTwoLevelsPrompt: false }))}
                      className="text-blue-500 focus:ring-blue-500"
                    />
                    <Label htmlFor="useDefaultTwoLevelsPrompt" className="cursor-pointer text-slate-200">
                      Use Default Two-Levels-Deep Prompt
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="useCustomTwoLevelsPrompt"
                      name="twoLevelsPromptType"
                      checked={formData.useCustomTwoLevelsPrompt}
                      onChange={() => setFormData(prev => ({ ...prev, useCustomTwoLevelsPrompt: true }))}
                      className="text-blue-500 focus:ring-blue-500"
                    />
                    <Label htmlFor="useCustomTwoLevelsPrompt" className="cursor-pointer text-slate-200">
                      Use Custom Two-Levels-Deep Prompt
                    </Label>
                  </div>
                </div>
                
                {/* Custom Two-Levels-Deep Prompt Input */}
                {/* {formData.useCustomTwoLevelsPrompt && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="customTwoLevelsPrompt" className="text-slate-300 font-medium">
                        Custom Two-Levels-Deep Instructions
                      </Label>
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                          {formData.customTwoLevelsPrompt.length} chars
                        </Badge>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={resetToDefaultTwoLevelsPrompt}
                          className="h-7 px-2 text-xs border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                          Reset to Default
                        </Button>
                      </div>
                    </div>
                    <div className="relative">
                      <Textarea
                        id="customTwoLevelsPrompt"
                        name="customTwoLevelsPrompt"
                        value={formData.customTwoLevelsPrompt}
                        onChange={handleInputChange}
                        placeholder="Enter your custom two-levels-deep instructions..."
                        className="min-h-[200px] resize-none bg-slate-800/50 border-slate-600 text-slate-100 placeholder:text-slate-400 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 font-mono text-sm"
                        rows={8}
                      />
                      <div className="absolute bottom-3 right-3 text-xs text-slate-500">
                        Use {'{client_name}'}, {'{transcript_text}'}, {'{past_relevant_proposals}'}, {'{games_data}'}, {'{training_sessions_data}'} as placeholders
                      </div>
                    </div>
                    <div className="text-xs text-slate-400 bg-slate-800/50 p-3 rounded border border-slate-600">
                      <strong>💡 Tip:</strong> The custom two-levels-deep prompt will completely replace the default refinement instructions. This controls how the AI expands and details each section of the proposal.
                    </div>
                  </div>
                )}
                
                {/* Default Two-Levels-Deep Prompt Preview */}
                {/* {!formData.useCustomTwoLevelsPrompt && (
                  <div className="p-4 border border-slate-600 rounded-lg bg-slate-800/30 backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <CheckCircle2 className="h-4 w-4 text-green-400" />
                        <span className="text-slate-200 font-medium">Using Default Two-Levels-Deep Prompt</span>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(formData.customTwoLevelsPrompt);
                          toast.success('Default two-levels-deep prompt copied to clipboard');
                        }}
                        className="h-7 px-2 text-xs border-slate-600 text-slate-300 hover:bg-slate-700"
                      >
                        Copy Default
                      </Button>
                    </div>
                    <p className="text-sm text-slate-400">
                      The AI will use the standard Brainayan two-levels-deep refinement instructions. 
                      This includes detailed requirements for expanding every section with substantial depth and concrete examples.
                    </p>
                  </div>
                )}
              </div> */}
              
              {/* NEW: Reference Proposals Selection Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-lg font-semibold text-slate-200">Reference Proposals</Label>
                    <p className="text-sm text-slate-400">
                      Select specific proposals to use as references (optional)
                    </p>
                  </div>
                  <Badge className="bg-gradient-to-r from-green-500 to-blue-500 text-white border-0">
                    <FileText className="h-3 w-3 mr-1" />
                    {selectedReferenceIds.length} Selected
                  </Badge>
                </div>
                
                {/* Search Bar */}
                <div className="space-y-2">
                  <Label htmlFor="referenceSearch" className="text-slate-300">Search Proposals:</Label>
                  <Input
                    id="referenceSearch"
                    value={referenceSearchTerm}
                    onChange={(e) => setReferenceSearchTerm(e.target.value)}
                    placeholder="Search by proposal title..."
                    className="h-10 bg-slate-800/50 border-slate-600 text-slate-100 placeholder:text-slate-400 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                  />
                </div>
                
                {/* Proposals List */}
                <div className="max-h-60 overflow-y-auto border border-slate-600 rounded-lg bg-slate-800/30 backdrop-blur-sm">
                  {filteredReferenceProposals.length === 0 ? (
                    <div className="p-4 text-center text-slate-400">
                      {referenceSearchTerm ? 'No proposals match your search' : 'Loading proposals...'}
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {filteredReferenceProposals.map((proposal) => (
                        <label
                          key={proposal.id}
                          className="flex items-center space-x-3 p-2 rounded hover:bg-slate-700/50 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedReferenceIds.includes(proposal.id)}
                            onChange={() => handleReferenceSelection(proposal.id)}
                            className="text-blue-500 focus:ring-blue-500 rounded"
                          />
                          <span className="text-sm text-slate-200 flex-1">
                            {proposal.title}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Clear Selection Button */}
                {selectedReferenceIds.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSelectedReferenceIds([])}
                    className="w-full border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-slate-100"
                    size="sm"
                  >
                    Clear All Selections
                  </Button>
                )}
                
                <div className="text-xs text-slate-400 bg-slate-800/50 p-3 rounded border border-slate-600">
                  <strong>💡 Tip:</strong> If you don't select any reference proposals, the AI will automatically find relevant ones using vector search.
                </div>
              </div>

              {/* Training Resources Selection Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-lg font-semibold text-slate-200">Training Resources</Label>
                    <p className="text-sm text-slate-400">
                      Select specific resources to include in your proposal (optional)
                    </p>
                  </div>
                  <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
                    <Sparkles className="h-3 w-3 mr-1" />
                    {Object.values(selectedResources).flat().length} Selected
                  </Badge>
                </div>
                
                {/* Resource Categories Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { key: 'psychometrics', label: 'Psychometrics', icon: '🧠', color: 'from-blue-500 to-cyan-500' },
                    { key: 'frameworks', label: 'Frameworks', icon: '📋', color: 'from-green-500 to-emerald-500' },
                    { key: 'tools', label: 'Tools', icon: '🛠️', color: 'from-orange-500 to-red-500' },
                    { key: 'games', label: 'Games', icon: '🎮', color: 'from-purple-500 to-pink-500' }
                  ].map(category => (
                    <div key={category.key} className="p-3 bg-slate-800/30 rounded-lg border border-slate-600">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-lg">{category.icon}</span>
                        <span className="text-xs text-slate-400">{selectedResources[category.key]?.length || 0}</span>
                      </div>
                      <p className="text-sm text-slate-300 font-medium">{category.label}</p>
                    </div>
                  ))}
                </div>
                
                {/* Browse Resources Button */}
                <Button
                  type="button"
                  onClick={() => setShowCatalogBrowser(true)}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Browse & Select Resources
                </Button>
                
                {/* Selected Resources Preview */}
                {Object.values(selectedResources).flat().length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-slate-300">Selected Resources:</Label>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {Object.entries(selectedResources).map(([category, resources]) =>
                        resources.map(resource => (
                          <div key={resource.id} className="flex items-center justify-between p-2 bg-slate-800/50 rounded border border-slate-600">
                            <span className="text-sm text-slate-200">{resource.name}</span>
                            <button
                              type="button"
                              onClick={() => handleResourceSelect(resource)}
                              className="text-red-400 hover:text-red-300 text-xs"
                            >
                              Remove
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setSelectedResources({ psychometrics: [], frameworks: [], tools: [], games: [] })}
                      className="w-full border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-slate-100"
                      size="sm"
                    >
                      Clear All Resources
                    </Button>
                  </div>
                )}
                
                <div className="text-xs text-slate-400 bg-slate-800/50 p-3 rounded border border-slate-600">
                  <strong>💡 Tip:</strong> Selected resources will be included in your proposal with detailed descriptions and usage instructions.
                </div>
              </div>

              {/* Upload New Proposal */}
              <div className="space-y-3">
                <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-600">
                  <Label className="text-slate-300 font-medium mb-3 block">
                    Upload New Proposal
                  </Label>
                  <p className="text-sm text-slate-400 mb-3">
                    Upload a .pptx, .pdf, or .docx file to add it to the reference proposals list.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pptx,.pdf,.docx"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="proposal-upload"
                      />
                      <label
                        htmlFor="proposal-upload"
                        className="flex-1 px-4 py-2 border border-slate-600 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 cursor-pointer transition-colors text-sm"
                      >
                        {uploadFile ? uploadFile.name : 'Choose file...'}
                      </label>
                      {uploadFile && (
                        <Button
                          type="button"
                          onClick={uploadProposalFile}
                          disabled={isUploading}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          size="sm"
                        >
                          {isUploading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Upload
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                    
                    {uploadFile && (
                      <div className="text-xs text-slate-400">
                        File: {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Reference Learning Toggle */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg border border-slate-600">
                  <div className="flex-1">
                    <Label className="text-slate-300 font-medium">
                      Use Past Proposals as Reference
                    </Label>
                    <p className="text-sm text-slate-400 mt-1">
                      When enabled, AI learns from past proposals. When disabled, generates completely original content.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      type="button"
                      onClick={() => setUseReferences(!useReferences)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 ${
                        useReferences ? 'bg-blue-600' : 'bg-slate-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          useReferences ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Model Selection */}
              <div className="space-y-4">
                <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-600">
                  <Label className="text-slate-300 font-medium mb-3 block">
                    Model Selection
                  </Label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Main Model Selection */}
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-sm">Main Model (Stage 1 & 2)</Label>
                      <select
                        value={selectedMainModel}
                        onChange={(e) => setSelectedMainModel(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="deepseek-v3.1">DeepSeek v3.1 (deepinfra/fp4)</option>
                        <option value="qwen/qwen-max">Qwen Max (chutes)</option>
                        <option value="meta-llama-4">Meta Llama 4 (meta)</option>
                        <option value="gpt-oss-20b">GPT-OSS-20B (atlas-cloud/fp8)</option>
                        <option value="gpt-oss-120b">GPT-OSS-120B (ncompass)</option>
                        <option value="qwen3-thinking">Qwen3 Thinking (chutes)</option>
                        <option value="qwen/qwen3-30b-a3b">Qwen3 30B A3B (chutes) - Thinking Mode</option>
                        <option value="perplexity-sonar-deep-research">Perplexity Sonar Deep Research (perplexity)</option>
                      </select>
                    </div>

                    {/* Distill Model Selection */}
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-sm">Distill Model</Label>
                      <select
                        value={selectedDistillModel}
                        onChange={(e) => setSelectedDistillModel(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="deepseek-v3.1">DeepSeek v3.1 (deepinfra/fp4)</option>
                        <option value="qwen/qwen-max">Qwen Max (chutes)</option>
                        <option value="meta-llama-4">Meta Llama 4 (meta)</option>
                        <option value="gpt-oss-20b">GPT-OSS-20B (atlas-cloud/fp8)</option>
                        <option value="gpt-oss-120b">GPT-OSS-120B (ncompass)</option>
                        <option value="qwen3-thinking">Qwen3 Thinking (chutes)</option>
                        <option value="qwen/qwen3-30b-a3b">Qwen3 30B A3B (chutes) - Thinking Mode</option>
                        <option value="perplexity-sonar-deep-research">Perplexity Sonar Deep Research (perplexity)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
            
            <CardFooter>
              <div className="w-full relative">
                <div className="p-[2px] bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 rounded-xl group">
                  <Button
                    onClick={generateProposal}
                    disabled={generationStatus === 'generating'}
                    className="w-full h-14 text-base bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-[10px] transition-all duration-300 group-hover:bg-slate-700/80"
                    size="lg"
                  >
                  {generationStatus === 'generating' ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating Proposal...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate AI Proposal
                    </>
                  )}
                  </Button>
                </div>
              </div>
            </CardFooter>
          </Card>
          </div>
          
          {/* Generation Status Card */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 via-cyan-500 to-blue-500 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
            <Card className="relative bg-slate-900/80 backdrop-blur-xl border-slate-700/50 hover:border-slate-600/50 transition-all duration-500">
              <CardHeader className="space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <Zap className="h-5 w-5 text-purple-400" />
                    </div>
                    <CardTitle className="text-xl text-slate-100">Generation Status</CardTitle>
                  </div>
                  <Badge className="bg-gradient-to-r from-purple-500 to-cyan-500 text-white border-0 shadow-lg">
                    <Monitor className="h-3 w-3 mr-1" />
                    Live Tracking
                  </Badge>
                </div>
                <CardDescription className="text-slate-400">
                  Real-time AI proposal generation progress
                </CardDescription>
              </CardHeader>
            <CardContent className="space-y-6">
              {generationStatus === 'idle' && (
                <div className="text-center py-16">
                  <div className="relative w-24 h-24 mx-auto mb-6">
                    <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur opacity-20 animate-pulse"></div>
                    <div className="relative w-24 h-24 bg-slate-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-slate-700">
                      <FileText className="w-12 h-12 text-blue-400" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-bold mb-3 text-slate-100">Ready to Generate</h3>
                  <p className="text-slate-300 max-w-md mx-auto mb-6 leading-relaxed">
                    Fill in the client details and transcript, then click "Generate AI Proposal" to create your professional proposal
                  </p>
                  <div className="inline-flex items-center gap-3">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                    <Badge className="bg-gradient-to-r from-green-500 to-blue-500 text-white border-0 shadow-lg">
                      AI System Ready
                    </Badge>
                  </div>
                </div>
              )}
              {generationStatus === 'generating' && (
                <div className="space-y-8">
                  <div className="text-center">
                    <div className="relative w-24 h-24 mx-auto mb-6">
                      <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 rounded-full blur opacity-30 animate-pulse"></div>
                      <div className="relative w-24 h-24 bg-slate-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-slate-700">
                        <div className="absolute inset-2 rounded-full border-2 border-blue-400/30 border-t-blue-400 animate-spin"></div>
                        <Sparkles className="w-10 h-10 text-blue-400 animate-pulse" />
                      </div>
                    </div>
                    <h3 className="text-3xl font-bold mb-3 text-slate-100">Generating Your Proposal</h3>
                    <p className="text-slate-300 mb-6 max-w-md mx-auto">{progressMessage}</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-400">Progress</span>
                      <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-white border-0 shadow-lg">
                        {generationProgress}% Complete
                      </Badge>
                    </div>
                    <div className="relative">
                      <Progress value={generationProgress} className="h-3 bg-slate-800 border border-slate-700" />
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full animate-shimmer"></div>
                    </div>
                  </div>
                  
                  <div className="p-4 border border-slate-600 rounded-lg bg-slate-800/30 backdrop-blur-sm">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg flex items-center justify-center mt-0.5">
                        <Zap className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="font-medium text-slate-200">Real-time AI Processing</h4>
                        <p className="text-sm text-slate-400">
                          Our AI is crafting your proposal with precision. This typically takes 1-2 minutes depending on complexity.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {generationStatus === 'success' && generatedProposal && (
                <div className="space-y-8">
                  <div className="text-center">
                    <div className="relative w-24 h-24 mx-auto mb-6">
                      <div className="absolute -inset-2 bg-gradient-to-r from-green-500 to-blue-500 rounded-full blur opacity-30 animate-pulse"></div>
                      <div className="relative w-24 h-24 bg-slate-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-slate-700">
                        <CheckCircle2 className="w-12 h-12 text-green-400" />
                      </div>
                    </div>
                    <h3 className="text-3xl font-bold mb-3 text-slate-100">Proposal Generated Successfully!</h3>
                    <p className="text-slate-300 mb-6">
                      {generatedProposal.proposal?.client_name || 'Unknown Client'} - {generatedProposal.proposal?.title || 'Generated Proposal'}
                    </p>
                  </div>
                  
                  <div className="p-4 border border-slate-600 rounded-lg bg-gradient-to-r from-green-500/10 to-blue-500/10 backdrop-blur-sm">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-lg flex items-center justify-center mt-0.5">
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="font-medium text-slate-200">Proposal Ready</h4>
                        <p className="text-sm text-slate-400">
                          Your AI-generated proposal is ready for download or preview
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* LLM Outputs Display */}
                  {(() => {
                    console.log('Rendering LLM outputs section, llmOutputs state:', llmOutputs);
                    console.log('LLM outputs ref:', llmOutputsRef.current);
                    return null;
                  })()}
                  
                  {/* Debug: Show restore button if ref has data but state is empty */}
                  {llmOutputsRef.current && Object.keys(llmOutputsRef.current).length > 0 && 
                   (!llmOutputs || Object.keys(llmOutputs).length === 0) && (
                    <div className="p-4 border border-yellow-600 rounded-lg bg-yellow-500/10 backdrop-blur-sm mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <AlertCircle className="w-5 h-5 text-yellow-400" />
                          <div>
                            <h5 className="font-medium text-yellow-200">LLM Outputs Available in Memory</h5>
                            <p className="text-sm text-yellow-300">State was reset, but outputs are preserved</p>
                          </div>
                        </div>
                        <Button 
                          onClick={restoreLlmOutputs} 
                          className="bg-yellow-600 hover:bg-yellow-700 text-white"
                          size="sm"
                        >
                          Restore Outputs
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Debug: Show check status button if no proposal data */}
                  {(!generatedProposal || !generatedProposal.proposal) && (
                    <div className="p-4 border border-blue-600 rounded-lg bg-blue-500/10 backdrop-blur-sm mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <AlertCircle className="w-5 h-5 text-blue-400" />
                          <div>
                            <h5 className="font-medium text-blue-200">No Proposal Data Available</h5>
                            <p className="text-sm text-blue-300">Download buttons require proposal data. Check if generation completed.</p>
                          </div>
                        </div>
                        <Button 
                          onClick={() => {
                            const activeSession = localStorage.getItem('active_generation_session');
                            if (activeSession) {
                              console.log('Checking status of active session:', activeSession);
                              pollProgress(activeSession);
                            } else {
                              toast.error('No active generation session found');
                            }
                          }} 
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          size="sm"
                        >
                          Check Generation Status
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Use ref data if state is empty - prevents overwrites */}
                  {(llmOutputs && Object.keys(llmOutputs).length > 0) || 
                   (llmOutputsRef.current && Object.keys(llmOutputsRef.current).length > 0) ? (
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-slate-200">AI Generation Process</h4>
                      
                      {/* Stage 1 Output */}
                      {(llmOutputs.stage1_llm_output || llmOutputsRef.current.stage1_llm_output) && (
                        <div className="p-4 border border-slate-600 rounded-lg bg-slate-800/30 backdrop-blur-sm">
                          <div className="flex items-start space-x-3 mb-3">
                            <div className="w-8 h-8 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg flex items-center justify-center mt-0.5">
                              <span className="text-blue-400 text-sm font-bold">1</span>
                            </div>
                            <div className="flex-1">
                              <h5 className="font-medium text-slate-200 mb-2">Stage 1: Basic Proposal Generation</h5>
                              <div className="bg-slate-900/50 rounded p-3 border border-slate-700">
                                <p className="text-slate-300 text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                                  {typeof (llmOutputs.stage1_llm_output || llmOutputsRef.current.stage1_llm_output) === 'string' ? 
                                   (llmOutputs.stage1_llm_output || llmOutputsRef.current.stage1_llm_output) : 'Content not available'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Stage 2 Output */}
                      {(llmOutputs.stage2_llm_output || llmOutputsRef.current.stage2_llm_output) && (
                        <div className="p-4 border border-slate-600 rounded-lg bg-slate-800/30 backdrop-blur-sm">
                          <div className="flex items-start space-x-3 mb-3">
                            <div className="w-8 h-8 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 rounded-lg flex items-center justify-center mt-0.5">
                              <span className="text-blue-400 text-sm font-bold">2</span>
                            </div>
                            <div className="flex-1">
                              <h5 className="font-medium text-slate-200 mb-2">Stage 2: Two-Levels-Deep Refinement</h5>
                              <div className="bg-slate-900/50 rounded p-3 border border-slate-700">
                                <p className="text-slate-300 text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                                  {typeof (llmOutputs.stage2_llm_output || llmOutputsRef.current.stage2_llm_output) === 'string' ? 
                                   (llmOutputs.stage2_llm_output || llmOutputsRef.current.stage2_llm_output) : 'Content not available'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <Button onClick={() => downloadProposal('pdf')} className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white shadow-lg" size="sm">
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                      <Button onClick={() => downloadProposal('md')} className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white shadow-lg" size="sm">
                        <Download className="w-4 h-4 mr-2" />
                        Download DOCX
                      </Button>
                      <Button onClick={() => downloadProposal('pptx')} className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white shadow-lg" size="sm">
                        <Download className="w-4 h-4 mr-2" />
                        Download PPTX
                      </Button>
                    </div>
                    <Button onClick={previewProposal} variant="outline" className="w-full border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-slate-100" size="sm">
                      <Eye className="w-4 h-4 mr-2" />
                      Preview Proposal
                    </Button>
                    <Button onClick={resetForm} className="w-full bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600 text-white shadow-lg" size="sm">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Another Proposal
                    </Button>
                  </div>
                </div>
              )}
            
              {generationStatus === 'error' && (
                <div className="text-center py-16">
                  <div className="relative w-24 h-24 mx-auto mb-6">
                    <div className="absolute -inset-2 bg-gradient-to-r from-red-500 to-orange-500 rounded-full blur opacity-30 animate-pulse"></div>
                    <div className="relative w-24 h-24 bg-slate-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-slate-700">
                      <AlertCircle className="w-12 h-12 text-red-400" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-bold mb-3 text-slate-100">Generation Failed</h3>
                  <p className="text-slate-300 mb-6 max-w-md mx-auto">
                    There was an error generating your proposal. Please check your input and try again.
                  </p>
                  
                  <div className="p-4 border border-slate-600 rounded-lg bg-gradient-to-r from-red-500/10 to-orange-500/10 backdrop-blur-sm mb-6 max-w-md mx-auto">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-lg flex items-center justify-center mt-0.5">
                        <AlertCircle className="w-5 h-5 text-red-400" />
                      </div>
                      <div className="space-y-1 text-left">
                        <h4 className="font-medium text-slate-200">Common Issues</h4>
                        <ul className="text-sm text-slate-400 space-y-1">
                          <li>• Transcript text might be too short</li>
                          <li>• Client name might be missing</li>
                          <li>• Network connection issue</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <Button onClick={resetForm} className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white shadow-lg" size="lg">
                    <Play className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        </div>
      </div>
      
      {/* Catalog Browser Modal */}
      {showCatalogBrowser && (
        createPortal(
          <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto" style={{ zIndex: 100000 }}>
            <div className="bg-slate-900 rounded-lg border border-slate-700 overflow-hidden my-4" style={{ width: 'min(95vw, 1100px)' }}>
              <div className="flex items-center justify-between p-4 border-b border-slate-700 flex-shrink-0">
                <h2 className="text-xl font-semibold text-slate-200">Training Resources Catalog</h2>
                <button
                  onClick={() => setShowCatalogBrowser(false)}
                  className="text-slate-400 hover:text-slate-200 transition-colors"
                >
                  ✕
                </button>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: '80vh' }}>
                <CatalogBrowser
                  onResourceSelect={handleResourceSelect}
                  selectedResources={selectedResources}
                />
              </div>
            </div>
          </div>,
          document.body
        )
      )}
    </div>
  );
};


export default ProposalGenerator; 